import {
  Button,
  Flex,
  Grid,
  KeyboardArrowDownRoundedIcon,
  Popover,
  Text,
} from '@shared/components';
import { useState } from 'react';

import { ROW_PER_PAGE } from '../../constants';
import { NavigatorLink } from '../navigator-link';
import { QuestionPagination } from '../question-pagination';

export interface QuestionNavigatorProps {
  onJump: (to: number) => void;
  totalQuestions: number;
  currentQuestion: number;
}

export default function QuestionNavigator(props: Readonly<QuestionNavigatorProps>) {
  const { totalQuestions, currentQuestion, onJump } = props;

  const [currentPage, setCurrentPage] = useState<number>(1);

  const handleChangePage = (value: number) => {
    setCurrentPage(value);
  };

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isOpen = Boolean(anchorEl);
  const id = isOpen ? 'question-navigator' : undefined;

  return (
    <>
      <Button
        aria-describedby={id}
        onClick={handleClick}
        sx={(theme) => ({
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          bgcolor: theme.palette.grey[100],
        })}
        endIcon={<KeyboardArrowDownRoundedIcon />}
      >
        Question {currentQuestion} of {totalQuestions}
      </Button>
      <Popover
        id={id}
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            gap: theme.spacing(4),
            p: theme.spacing(4),
            textAlign: 'center',
          })}
        >
          <Text variant='body1Semibold'>Question Navigator</Text>
          <QuestionPagination
            currentQuestion={currentQuestion}
            currentPage={currentPage}
            totalQuestions={totalQuestions}
            onChange={handleChangePage}
          />
          <Grid
            sx={(theme) => ({
              gridTemplateColumns: 'repeat(10, 1fr)',
              columnGap: theme.spacing(1.5),
              rowGap: theme.spacing(1),
            })}
          >
            {Array.from(
              {
                length:
                  ROW_PER_PAGE * currentPage > totalQuestions
                    ? totalQuestions % ROW_PER_PAGE
                    : ROW_PER_PAGE,
              },
              (_, index) => {
                const question = currentPage * ROW_PER_PAGE - ROW_PER_PAGE + index + 1;
                return (
                  <Grid key={question}>
                    <NavigatorLink
                      label={question}
                      onClick={onJump}
                      selected={currentQuestion === question}
                    />
                  </Grid>
                );
              },
            )}
          </Grid>
        </Flex>
      </Popover>
    </>
  );
}
